import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_MODE = process.env.REACT_APP_STATIC_MODE === "true";
const STATIC_EMAIL = "juan@sahagunlandscape.com";

const Stars = ({ n }) => (
  <div className="flex gap-1" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${i <= n ? "text-[#2d4a22]" : "text-[#2d4a22]/20"}`}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.2 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z" />
      </svg>
    ))}
  </div>
);

const INITIAL = { name: "", city: "", rating: "5", text: "" };
const inputCls =
  "px-4 py-3 bg-[#faf8f5] border border-[#2d4a22]/30 text-sm focus:border-[#2d4a22] focus:outline-none w-full";
const labelCls = "text-xs uppercase font-bold tracking-wider text-[#1f281e]";

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (STATIC_MODE) return; // static hosting has no review database
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data))
      .catch(() => setReviews([]));
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (STATIC_MODE) {
        // Static hosting: send the review to Juan's inbox via FormSubmit
        const res = await fetch(`https://formsubmit.co/ajax/${STATIC_EMAIL}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            _subject: `New Customer Review - ${form.rating}/5`,
            _template: "table",
            name: form.name,
            city: form.city || "-",
            rating: `${form.rating}/5`,
            review: form.text,
          }),
        });
        if (!res.ok) throw new Error("send failed");
      } else {
        await axios.post(`${API}/reviews`, { ...form, rating: parseInt(form.rating, 10) });
      }
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Thank you! Your review was sent for approval.");
    } catch (err) {
      toast.error("Could not send your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-32 bg-[#faf8f5] text-[#1f281e] border-b border-[#2d4a22]/10">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="reveal">
            <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
              Customer Voices
            </span>
            <h2 className="font-display text-3xl md:text-5xl uppercase text-[#1f281e]">What Neighbors Say</h2>
          </div>
          <p className="reveal reveal-d1 text-sm text-[#3d4b3b] max-w-sm">
            Real words from real yards across Paramount and Los Angeles County.
          </p>
        </div>

        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {reviews.map((r, i) => (
              <blockquote
                key={r.id}
                data-testid={`review-card-${i + 1}`}
                className={`reveal reveal-d${(i % 3) + 1} p-8 bg-[#f2ece1] border border-[#2d4a22]/10 flex flex-col gap-4`}
              >
                <Stars n={r.rating} />
                <p className="text-sm text-[#3d4b3b] leading-relaxed flex-1">“{r.text}”</p>
                <footer className="text-xs uppercase tracking-widest font-bold text-[#1f281e]">
                  {r.name}{r.city ? <span className="text-[#8a8272] font-normal"> — {r.city}</span> : null}
                </footer>
              </blockquote>
            ))}
          </div>
        ) : (
          <div className="reveal p-8 border border-[#2d4a22]/20 bg-[#f2ece1] mb-16" data-testid="reviews-empty">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-2.5 h-2.5 bg-[#4ade80] rounded-full organic-grow" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#1f281e]">First Reviews On The Way</h3>
            </div>
            <p className="text-xs text-[#3d4b3b] leading-relaxed">
              We are collecting reviews from our recent Paramount and Southern California projects — they will appear
              here as customers share them. Worked with us? Be the first to leave one below.
            </p>
          </div>
        )}

        <div className="max-w-2xl mx-auto reveal">
          <div className="bg-[#f2ece1] p-8 md:p-10 border border-[#2d4a22]/20 shadow-sm">
            <h3 className="font-display text-2xl uppercase mb-1">Share Your Experience</h3>
            <p className="text-xs text-[#3d4b3b] mb-8">
              Reviews are published after a quick check by our team.
            </p>

            {submitted ? (
              <div className="text-center py-8" data-testid="review-success">
                <svg className="w-12 h-12 mx-auto mb-4 text-[#2d4a22]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h4 className="font-display text-xl uppercase mb-2">Review Received</h4>
                <p className="text-sm text-[#3d4b3b] mb-6">Thank you — it will appear here once approved.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="review-another-btn"
                  className="px-8 py-3 border border-[#2d4a22] text-[#2d4a22] text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#2d4a22]/5 transition-colors"
                >
                  Write Another
                </button>
              </div>
            ) : (
              <form className="grid grid-cols-1 sm:grid-cols-2 gap-6" onSubmit={handleSubmit} data-testid="review-form">
                <div className="flex flex-col gap-1">
                  <label htmlFor="review-name" className={labelCls}>Your Name</label>
                  <input id="review-name" data-testid="review-name-input" type="text" required value={form.name} onChange={update("name")} className={inputCls} placeholder="First name" />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="review-city" className={labelCls}>City</label>
                  <input id="review-city" data-testid="review-city-input" type="text" value={form.city} onChange={update("city")} className={inputCls} placeholder="e.g. Paramount" />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="review-rating" className={labelCls}>Rating</label>
                  <select id="review-rating" data-testid="review-rating-select" value={form.rating} onChange={update("rating")} className={inputCls}>
                    <option value="5">5 - Excellent</option>
                    <option value="4">4 - Great</option>
                    <option value="3">3 - Good</option>
                    <option value="2">2 - Fair</option>
                    <option value="1">1 - Poor</option>
                  </select>
                </div>
                <div className="sm:col-span-2 flex flex-col gap-1">
                  <label htmlFor="review-text" className={labelCls}>Your Review</label>
                  <textarea id="review-text" data-testid="review-text-textarea" rows="3" required value={form.text} onChange={update("text")} className={inputCls} placeholder="How was the crew, the price, the result?" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={submitting} data-testid="review-submit-btn" className="w-full py-4 bg-[#2d4a22] text-[#faf8f5] text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#1a2d14] transition-colors disabled:opacity-60">
                    {submitting ? "Sending..." : "Submit Review"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
